/**
 * Floating Liquid Glass tab bar — verbatim port of mobile.html `.tabbar`.
 * 4 tabs: Today / Chart / Transits / AI.
 */
import { Link, useLocation } from "@tanstack/react-router";
import type { ReactElement } from "react";

import { useT } from "@/i18n";
import { hapticSelection } from "@/lib/tg";

type TabKey = "today" | "chart" | "transits" | "ai";

interface TabDef {
  readonly key: TabKey;
  /**
   * Translation key under `nav.*` in @astrology/i18n. Uses the short `nav.tab*`
   * variants (e.g. "Карта", "AI") — the full `nav.chart`/`nav.ai` labels are
   * drawer-length and wrap inside the 56px floating bar on phones.
   */
  readonly labelKey: string;
  readonly to: string;
  readonly icon: ReactElement;
}

const ICON_PROPS = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  viewBox: "0 0 24 24",
  "aria-hidden": true,
  focusable: false,
  role: "presentation",
} as const;

const TABS: readonly TabDef[] = [
  {
    key: "today",
    labelKey: "nav.tabToday",
    to: "/",
    icon: (
      // biome-ignore lint/a11y/noSvgWithoutTitle: decorative icon paired with visible label below
      <svg {...ICON_PROPS}>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    key: "chart",
    labelKey: "nav.tabChart",
    to: "/chart",
    icon: (
      // biome-ignore lint/a11y/noSvgWithoutTitle: decorative icon paired with visible label below
      <svg {...ICON_PROPS}>
        <circle cx="12" cy="12" r="9" />
        <line x1="12" y1="3" x2="12" y2="21" />
        <line x1="3" y1="12" x2="21" y2="12" />
      </svg>
    ),
  },
  {
    key: "transits",
    labelKey: "nav.tabTransits",
    to: "/transits",
    icon: (
      // biome-ignore lint/a11y/noSvgWithoutTitle: decorative icon paired with visible label below
      <svg {...ICON_PROPS}>
        <path d="M12 3l9 6-9 6-9-6z" />
        <path d="M3 15l9 6 9-6" />
      </svg>
    ),
  },
  {
    key: "ai",
    labelKey: "nav.tabAi",
    to: "/ai",
    icon: (
      // biome-ignore lint/a11y/noSvgWithoutTitle: decorative icon paired with visible label below
      <svg {...ICON_PROPS}>
        <path d="M21 12a9 9 0 1 1-9-9" />
        <path d="M21 4l-9 8-3-3" />
      </svg>
    ),
  },
];

export function TabBar({ active }: { readonly active: TabKey }): ReactElement {
  const loc = useLocation();
  const { t } = useT();
  return (
    <nav className="tabbar" aria-label={t("nav.menu")}>
      {TABS.map((tab) => {
        const isActive = tab.key === active || loc.pathname === tab.to;
        return (
          <Link
            key={tab.key}
            to={tab.to}
            className={`tab${isActive ? " active" : ""}`}
            onClick={() => {
              if (!isActive) hapticSelection();
            }}
          >
            {tab.icon}
            <span>{t(tab.labelKey)}</span>
          </Link>
        );
      })}
    </nav>
  );
}
